import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './illustrations.css';

const ViewIllustration = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const initialIndex = location.state?.index ?? 0;
    const models = location.state?.models ?? [];

    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [fullscreen, setFullscreen] = useState(false);
    const model = models[currentIndex];

    // Redirect if no data
    useEffect(() => {
        if (!model) {
            navigate('/illustrations');
        }
    }, [model, navigate]);



    const hasPrevious = currentIndex > 0;
    const hasNext = currentIndex < models.length - 1;

    const goPrevious = useCallback(() => {
        if (hasPrevious) {
            setCurrentIndex(prev => prev - 1);
        }
    }, [hasPrevious]);

    const goNext = useCallback(() => {
        if (hasNext) {
            setCurrentIndex(prev => prev + 1);
        }
    }, [hasNext]);

    // Keyboard navigation + Escape to close fullscreen
    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'ArrowRight') goNext();
            if (e.key === 'ArrowLeft') goPrevious();
            if (e.key === 'Escape') setFullscreen(false);
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [hasPrevious, hasNext, currentIndex]); // depend on these to ensure arrows work correctly with state

    // Prevent background scroll when fullscreen is open
    useEffect(() => {
        document.body.style.overflow = fullscreen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [fullscreen]);

    if (!model) return null;

    return (
        <div className="illus-root illus-viewer">
            {/* Header bar - Styled like ViewChapter */}
            <div className="illus-viewer-toolbar" style={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#f2f2f2',
                padding: '10px 20px',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                borderBottom: '1px solid #ddd'
            }}>
                <div style={{ color: 'black', fontWeight: 'bold', fontSize: '18px' }}>
                    {model.title}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <button
                        style={{
                            cursor: hasPrevious ? 'pointer' : 'not-allowed',
                            display: 'flex',
                            border: '1px solid lightgray',
                            color: 'black',
                            borderRadius: 5,
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 14,
                            fontWeight: 'bold',
                            backgroundColor: '#fff',
                            height: 40,
                            width: 150,
                            opacity: hasPrevious ? 1 : 0.5
                        }}
                        onClick={goPrevious}
                        disabled={!hasPrevious}
                    >
                        <img src={require('../../assets/left.png')} style={{ width: 16, height: 16, marginRight: 8 }} alt="" /> View Previous
                    </button>
                    <button
                        style={{
                            cursor: hasNext ? 'pointer' : 'not-allowed',
                            display: 'flex',
                            border: '1px solid lightgray',
                            color: 'black',
                            borderRadius: 5,
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 14,
                            fontWeight: 'bold',
                            backgroundColor: '#fff',
                            height: 40,
                            width: 150,
                            opacity: hasNext ? 1 : 0.5
                        }}
                        onClick={goNext}
                        disabled={!hasNext}
                    >
                        View Next <img src={require('../../assets/right.png')} style={{ width: 16, height: 16, marginLeft: 8 }} alt="" />
                    </button>
                </div>
            </div>

            {/* Image Viewer */}
            <div className="illus-viewer-stage" style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                backgroundColor: '#fff'
            }}>
                <div className="illus-viewer-card" style={{
                    maxWidth: '1000px',
                    width: '100%',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    backgroundColor: '#f9f9f9',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}>
                    <img
                        src={model.imageUrl}
                        alt={model.title}
                        style={{
                            maxWidth: '100%',
                            maxHeight: '70vh',
                            objectFit: 'contain',
                            display: 'block',
                            cursor: 'zoom-in'
                        }}
                        onClick={() => setFullscreen(true)}
                        title="Click to view fullscreen"
                    />
                    {model.caption && (
                        <div style={{
                            padding: '20px',
                            width: '100%',
                            borderTop: '1px solid #eee',
                            backgroundColor: '#fff',
                            fontSize: '16px',
                            lineHeight: '1.6',
                            color: '#444'
                        }}>
                            {model.caption}
                        </div>
                    )}
                </div>
            </div>
            {/* ── Fullscreen Lightbox ── */}
            {fullscreen && (
                <div
                    className="illus-lightbox"
                    onClick={() => setFullscreen(false)}
                    aria-label="Close fullscreen"
                    role="dialog"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 1000,
                        background: 'rgba(0, 0, 0, 0.96)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'zoom-out'
                    }}
                >
                    {/* Prev in lightbox */}
                    {hasPrevious && (
                        <button
                            className="illus-arrow-lightbox"
                            style={{
                                position: 'fixed',
                                left: '20px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'rgba(255, 255, 255, 0.1)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                color: '#fff',
                                width: '50px',
                                height: '50px',
                                borderRadius: '50%',
                                fontSize: '2rem',
                                cursor: 'pointer',
                                zIndex: 1001,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onClick={(e) => { e.stopPropagation(); goPrevious(); }}
                            aria-label="Previous"
                        >
                            ‹
                        </button>
                    )}

                    <img
                        src={model.imageUrl}
                        alt={model.title}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            maxWidth: '95vw',
                            maxHeight: '92vh',
                            objectFit: 'contain',
                            borderRadius: '6px',
                            boxShadow: '0 30px 80px rgba(0, 0, 0, 0.8)',
                            cursor: 'default'
                        }}
                    />

                    {/* Next in lightbox */}
                    {hasNext && (
                        <button
                            className="illus-arrow-lightbox"
                            style={{
                                position: 'fixed',
                                right: '20px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'rgba(255, 255, 255, 0.1)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                color: '#fff',
                                width: '50px',
                                height: '50px',
                                borderRadius: '50%',
                                fontSize: '2rem',
                                cursor: 'pointer',
                                zIndex: 1001,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onClick={(e) => { e.stopPropagation(); goNext(); }}
                            aria-label="Next"
                        >
                            ›
                        </button>
                    )}

                    {/* Close button */}
                    <button
                        style={{
                            position: 'fixed',
                            top: '20px',
                            right: '20px',
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            color: '#fff',
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            fontSize: '1.2rem',
                            cursor: 'pointer',
                            zIndex: 1001,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                        onClick={() => setFullscreen(false)}
                        aria-label="Close"
                    >
                        ✕
                    </button>

                    {/* Caption in lightbox */}
                    {model.title && (
                        <div
                            style={{
                                position: 'fixed',
                                bottom: '20px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                background: 'rgba(0, 0, 0, 0.6)',
                                color: '#fff',
                                padding: '8px 20px',
                                borderRadius: '20px',
                                fontSize: '0.9rem',
                                fontWeight: 500,
                                zIndex: 1001
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {model.title}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ViewIllustration;
